import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { z } from 'zod';
import { Paper } from '@mui/material';

import { ServiceAccessInput } from '../../organisms/settings/service-access-input';
import { Group } from '../../organisms/group';
import { useSelector } from '../../store/utils';
import { SwitchBox } from '../../molecules/switch-box';
import { ValueBox } from '../../molecules/value-box';
import { reverse } from 'lodash';
import { SimpleTable } from '../../molecules/simple-table';
import { FormattedDateTime } from '../../utils/formatted-datetime';
import { InputBox } from '../../molecules/input-box';
import { preprocessFloat } from '../../utils/zod-utils';

import { FlexibilityTest } from '../../organisms/flexibility/flexibility-command-test';
import { FlexibilityCommandType } from '../../shared';

const numericSchema = preprocessFloat(z.number().gte(0));

const DeltaGreen = () => {
  const [serviceActive, connected, lastMessages, activeCommand] = useSelector(
    ({ variables }) =>
      [
        variables.SERVICE_ACTIVE,
        variables.DELTA_GREEN_API_CONNECTED,
        variables.DELTA_GREEN_API_LAST_MESSAGES,
        variables.FLEXIBILITY_ACTIVE_COMMAND,
      ] as const
  );

  const lastMessagesReversed = useMemo(() => {
    return reverse(lastMessages ? [...lastMessages] : []);
  }, [lastMessages]);

  if (!serviceActive) {
    return (
      <Group
        layout="list"
        title={
          <FormattedMessage
            id="delta_green.title"
            defaultMessage="Delta Green"
          />
        }
      >
        <ServiceAccessInput />
      </Group>
    );
  }

  return (
    <>
      <Group
        layout="list"
        title={
          <FormattedMessage
            id="delta_green.title"
            defaultMessage="Delta Green"
          />
        }
      >
        <SwitchBox
          switchVariableId="DELTA_GREEN_API_ENABLED"
          title={
            <FormattedMessage
              id="delta_green.enabled"
              defaultMessage="Integrace zapnuta"
            />
          }
          fullWidth
        />
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="delta_green.connected"
              defaultMessage="Stav"
            />
          }
          value={connected ? 'Online' : 'Nepřipojeno'}
        />
        <InputBox
          title={
            <FormattedMessage
              id="delta_green.battery_max_power_in"
              defaultMessage="Maximální výkon baterie IN (W)"
            />
          }
          inputVariableId="DELTA_GREEN_BATTERY_MAX_POWER_IN"
          fullWidth
          schema={numericSchema}
        />
        <InputBox
          title={
            <FormattedMessage
              id="delta_green.battery_max_power_out"
              defaultMessage="Maximální výkon baterie OUT (W)"
            />
          }
          inputVariableId="DELTA_GREEN_BATTERY_MAX_POWER_OUT"
          fullWidth
          schema={numericSchema}
        />
        <InputBox
          title={
            <FormattedMessage
              id="delta_green.pv_orientation"
              defaultMessage="Orientace fotovoltaiky"
            />
          }
          inputVariableId="DELTA_GREEN_PV_ORIENTATION"
          fullWidth
          schema={numericSchema}
        />

        <SimpleTable
          title={
            <FormattedMessage
              id="delta_green.last_messages"
              defaultMessage="Poslední zprávy"
            />
          }
          rows={[
            [
              <FormattedMessage
                id="delta_green.last_messages.date"
                defaultMessage="Čas"
                key={0}
              />,
              <FormattedMessage
                id="delta_green.last_messages.topic"
                defaultMessage="Topic"
                key={1}
              />,
              <FormattedMessage
                id="delta_green.last_messages.payload"
                defaultMessage="Zpráva"
                key={2}
              />,
            ],
            ...lastMessagesReversed.map(({ date, payload, topic }) => {
              return [
                <FormattedDateTime value={date} key={0} />,
                JSON.stringify(topic),
                JSON.stringify(payload),
              ];
            }),
          ]}
          lastColBold={false}
          firstColBold={false}
          firstLineBold={true}
        />
      </Group>

      <Group
        layout="list"
        title={
          <FormattedMessage
            id="delta_green.business_flexibility.title"
            defaultMessage="Obchodní flexibilita"
          />
        }
      >
        <FlexibilityTest
          type={FlexibilityCommandType.DeltaGreenBusinessFlexibility}
        />

        <Paper>{JSON.stringify(activeCommand)}</Paper>
      </Group>
    </>
  );
};

export default DeltaGreen;
